import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
} from "@mui/material";
import { ResidentStatus } from "./utils/utils";

/* ================= STATUS (INT) ================= */

export default function AddResidentDialog({
  open,
  handleClose,
  handleSaveResident,
  resident,
}) {
  const isEdit = Boolean(resident);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    admissionDate: "",
    dischargeDate: "",
    status: ResidentStatus.ACTIVE,
  });

  const [errors, setErrors] = useState({});

  /* ================= LOAD RESIDENT ================= */

  useEffect(() => {
    if (resident) {
      setForm({
        firstName: resident.firstName || "",
        lastName: resident.lastName || "",
        gender: resident.gender || "",
        dateOfBirth: resident.dateOfBirth || "",
        admissionDate: resident.admissionDate || "",
        dischargeDate: resident.dischargeDate || "",
        status:
          typeof resident.status === "number"
            ? resident.status
            : ResidentStatus.ACTIVE,
      });
    }
  }, [resident]);

  /* ================= RULES ================= */

  const requiresDischargeDate =
    form.status === ResidentStatus.DISCHARGED ||
    form.status === ResidentStatus.DECEASED;

  /* ================= AUTO CLEANUP ================= */

  useEffect(() => {
    if (!requiresDischargeDate && form.dischargeDate) {
      setForm((prev) => ({ ...prev, dischargeDate: "" }));
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy.dischargeDate;
        return copy;
      });
    }
  }, [requiresDischargeDate]);

  /* ================= VALIDATION ================= */

  const validate = () => {
    const e = {};

    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";
    if (!form.gender) e.gender = "Gender is required";
    if (!form.dateOfBirth) e.dateOfBirth = "Date of birth is required";
    if (!form.admissionDate)
      e.admissionDate = "Admission date is required";

    if (requiresDischargeDate && !form.dischargeDate) {
      e.dischargeDate =
        "Discharge date is required for Discharged or Deceased status";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ================= SUBMIT ================= */

  const onSubmit = () => {
    if (!validate()) return;

    const payload = {
      ...form,
      dischargeDate:
        form.dischargeDate && !isNaN(Date.parse(form.dischargeDate))
          ? form.dischargeDate
          : null,
    };

    handleSaveResident(payload);
  };

  /* ================= RENDER ================= */

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Resident" : "Add Resident"}</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="First Name"
              required
              fullWidth
              size="small"
              value={form.firstName}
              error={!!errors.firstName}
              helperText={errors.firstName}
              onChange={(e) =>
                setForm({ ...form, firstName: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Last Name"
              required
              fullWidth
              size="small"
              value={form.lastName}
              error={!!errors.lastName}
              helperText={errors.lastName}
              onChange={(e) =>
                setForm({ ...form, lastName: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Gender"
              required
              fullWidth
              size="small"
              value={form.gender}
              error={!!errors.gender}
              helperText={errors.gender}
              onChange={(e) =>
                setForm({ ...form, gender: e.target.value })
              }
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              type="date"
              label="Date of Birth"
              required
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.dateOfBirth}
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth}
              onChange={(e) =>
                setForm({ ...form, dateOfBirth: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="date"
              label="Admission Date"
              required
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.admissionDate}
              error={!!errors.admissionDate}
              helperText={errors.admissionDate}
              onChange={(e) =>
                setForm({ ...form, admissionDate: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Status"
              fullWidth
              size="small"
              value={form.status}
              disabled={isEdit}
              helperText={
                isEdit
                  ? "Status changes are controlled from resident lifecycle actions."
                  : undefined
              }
              onChange={(e) =>
                setForm({ ...form, status: Number(e.target.value) })
              }
            >
              <MenuItem value={ResidentStatus.ACTIVE}>Active</MenuItem>
              <MenuItem value={ResidentStatus.INACTIVE}>
                Inactive
              </MenuItem>
            </TextField>
          </Grid>

          {/* <Grid item xs={12}>
                        <TextField
                            type="date"
                            label="Discharge Date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={form.dischargeDate}
                            required={requiresDischargeDate}
                            disabled={!requiresDischargeDate}
                            error={!!errors.dischargeDate}
                            helperText={
                                errors.dischargeDate ||
                                "Enabled only for Discharged or Deceased status"
                            }
                            onChange={(e) =>
                                setForm({ ...form, dischargeDate: e.target.value })
                            }
                        />
                    </Grid> */}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={onSubmit}>
          {isEdit ? "Save Changes" : "Add Resident"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
